import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Line } from "react-chartjs-2";
import { Chart, registerables } from "chart.js";

Chart.register(...registerables);

const resolveChartPeak = (series = []) => {
    const values = (Array.isArray(series) ? series : [])
        .flatMap((item) => (Array.isArray(item?.data) ? item.data : []))
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value));

    const peak = values.length ? Math.max(...values) : 0;
    return Math.max(20, Math.ceil(peak / 20) * 20);
};

const UserActivityChart = ({ chartPayload = null }) => {
    const labels = Array.isArray(chartPayload?.labels) ? chartPayload.labels : [];
    const series = Array.isArray(chartPayload?.series) ? chartPayload.series : [];

    const chartConfig = useMemo(() => {
        const yMax = resolveChartPeak(series);

        return {
            data: {
                labels,
                datasets: series.map((item) => ({
                    label: String(item?.label ?? "").trim(),
                    data: Array.isArray(item?.data) ? item.data : [],
                    borderColor: item?.borderColor || "#2563eb",
                    backgroundColor: "transparent",
                    borderWidth: 2,
                    tension: 0.4,
                    pointRadius: 4,
                    pointHoverRadius: 5,
                    pointBackgroundColor: "#111827",
                    pointBorderColor: "#111827",
                    pointBorderWidth: 1,
                    fill: false,
                })),
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: {
                    mode: "index",
                    intersect: false,
                },
                plugins: {
                    legend: {
                        position: "top",
                        align: "start",
                        labels: {
                            color: "#111827",
                            boxWidth: 36,
                            boxHeight: 10,
                            padding: 18,
                            font: {
                                size: 12,
                                weight: "600",
                            },
                        },
                    },
                    tooltip: {
                        mode: "index",
                        intersect: false,
                        backgroundColor: "#0f172a",
                        titleFont: { size: 12, weight: "600" },
                        bodyFont: { size: 12 },
                        padding: 10,
                        cornerRadius: 6,
                    },
                },
                scales: {
                    x: {
                        ticks: {
                            color: "#475569",
                            maxRotation: 45,
                            minRotation: 45,
                            autoSkip: true,
                            maxTicksLimit: 16,
                            font: { size: 11 },
                        },
                        grid: {
                            color: "rgba(148, 163, 184, 0.35)",
                            drawBorder: false,
                        },
                    },
                    y: {
                        beginAtZero: true,
                        max: yMax,
                        ticks: {
                            color: "#475569",
                            stepSize: 20,
                            font: { size: 11 },
                        },
                        grid: {
                            color: "rgba(148, 163, 184, 0.35)",
                            drawBorder: false,
                        },
                    },
                },
            },
        };
    }, [labels, series]);

    if (!labels.length || !series.length) {
        return (
            <div className="user-activity-chart-empty text-muted text-center py-5">
                No chart data available
            </div>
        );
    }

    return (
        <div className="user-activity-chart-wrap">
            <Line data={chartConfig.data} options={chartConfig.options} />
        </div>
    );
};

UserActivityChart.propTypes = {
    chartPayload: PropTypes.shape({
        labels: PropTypes.array,
        series: PropTypes.array,
    }),
};

export default UserActivityChart;
